import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import classNames from "classnames";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import {
    GridTable,
    GridTableHeader,
    GridTableRow,
} from "@design-system/Tables/GridTable/GridTable";
import { SortableHeader } from "@design-system/SortableHeader/SortableHeader";
import { ImporterContext } from "./importer.context";
import styles from "./ImporterPage.module.scss";
import { ImporterEmptyRow } from "./ImporterEmptyRow";
import { ImporterTableRow } from "./ImporterTableRow";
import { ImporterSort } from "./types";

const rowHeight = 40;
const headerHeight = 140;

export const ImporterPageContent: React.FC = () => {
    const { sortedRows, sort, setSort } = useContext(ImporterContext);

    const handleSortChange = useCallback(
        (newSort: ImporterSort) => {
            setSort(newSort);
        },
        [setSort],
    );
    const emptyRowsNumber = Math.max(
        5,
        Math.floor((window.innerHeight - headerHeight) / rowHeight) -
            sortedRows.length,
    );

    const [leftSticked, setLeftSticked] = useState(false);
    const [rightSticked, setRightSticked] = useState(false);

    const scrollbarsRef = useRef<Scrollbars>(null);

    const checkIfSticky = useCallback(() => {
        if (scrollbarsRef.current) {
            const scrollWidth = scrollbarsRef.current.getScrollWidth();
            const clientWidth = scrollbarsRef.current.getClientWidth();
            const scrollLeft = scrollbarsRef.current.getScrollLeft();
            setLeftSticked(scrollLeft > 0);
            setRightSticked(scrollLeft < scrollWidth - clientWidth);
        }
    }, []);

    useEffect(() => {
        checkIfSticky();
    }, [checkIfSticky, sortedRows]);

    return (
        <Scrollbars
            style={{ height: "calc(100% - 39px)", width: "100%" }}
            onScrollFrame={checkIfSticky}
            ref={scrollbarsRef}
        >
            <GridTable
                className={classNames(styles.table, {
                    [styles.leftSticked]: leftSticked,
                    [styles.rightSticked]: rightSticked,
                })}
                noBorder
            >
                <GridTableHeader
                    columnBorders
                    onClick={(e) => e.stopPropagation()}
                >
                    <GridTableRow columnBorders className={styles.stickyLeft}>
                        <div>#</div>
                        <div>
                            <SortableHeader
                                text="File name"
                                sort={sort}
                                asc={ImporterSort.FILE_NAME_ASC}
                                desc={ImporterSort.FILE_NAME_DESC}
                                onChange={handleSortChange}
                                defaultOrder="asc"
                            />
                        </div>
                    </GridTableRow>
                    <div>
                        <SortableHeader
                            text="Status"
                            sort={sort}
                            asc={ImporterSort.STATUS_ASC}
                            desc={ImporterSort.STATUS_DESC}
                            onChange={handleSortChange}
                            defaultOrder="asc"
                        />
                    </div>
                    <div>
                        <SortableHeader
                            text="Bank"
                            sort={sort}
                            asc={ImporterSort.BANK_ASC}
                            desc={ImporterSort.BANK_DESC}
                            onChange={handleSortChange}
                            defaultOrder="asc"
                        />
                    </div>
                    <div>Account #</div>
                    <div>
                        <SortableHeader
                            text="Account type"
                            sort={sort}
                            asc={ImporterSort.ACCOUNT_TYPE_ASC}
                            desc={ImporterSort.ACCOUNT_TYPE_DESC}
                            onChange={handleSortChange}
                            defaultOrder="asc"
                        />
                    </div>
                    <div>
                        <SortableHeader
                            text="Period start"
                            sort={sort}
                            asc={ImporterSort.PERIOD_START_ASC}
                            desc={ImporterSort.PERIOD_START_DESC}
                            onChange={handleSortChange}
                            defaultOrder="asc"
                        />
                    </div>
                    <div>
                        <SortableHeader
                            text="Period end"
                            sort={sort}
                            asc={ImporterSort.PERIOD_END_ASC}
                            desc={ImporterSort.PERIOD_END_DESC}
                            onChange={handleSortChange}
                            defaultOrder="asc"
                        />
                    </div>
                    <div>Entity</div>
                    <GridTableRow columnBorders className={styles.stickyRight}>
                        <div>
                            <SortableHeader
                                text="Transactions"
                                sort={sort}
                                asc={ImporterSort.TRANSACTIONS_ASC}
                                desc={ImporterSort.TRANSACTIONS_DESC}
                                onChange={handleSortChange}
                                defaultOrder="desc"
                            />
                        </div>
                    </GridTableRow>
                </GridTableHeader>

                {sortedRows.map((documentRow, index) => (
                    <ImporterTableRow
                        key={documentRow.id}
                        documentRow={documentRow}
                        rowNumber={index + 1}
                    />
                ))}

                {Array.from({ length: emptyRowsNumber }).map((_, index) => (
                    <ImporterEmptyRow
                        key={index}
                        rowNumber={sortedRows.length + index + 1}
                    />
                ))}
            </GridTable>
        </Scrollbars>
    );
};
